import { Navigate, Outlet, useLocation } from 'react-router'
import { errorMessage, signInUrl } from '../api/client'
import { useMe } from '../api/session'
import type { AreaId } from '../api/types'
import { homePath } from '../areas/areas'
import { FullPageError, FullPageLoading } from '../components/FullPageStatus'
import { AppShell } from '../shell/AppShell'
import { SessionContext, useSession } from './session-context'

/**
 * Route guard: loads `GET /api/v1/me`, sends anonymous users to sign-in (with `next`) and
 * users without any area to `/no-access`. Hiding routes here is cosmetic; the server
 * enforces every permission (P-UI-003).
 */
export function RequireSession() {
  const me = useMe()
  const location = useLocation()

  if (me.isPending) return <FullPageLoading />
  if (me.isError) {
    if (me.error.status === 401) {
      return <Navigate to={signInUrl(location.pathname + location.search)} replace />
    }
    if (me.error.status === 403 || me.error.code === 'no_access') {
      return <Navigate to="/no-access" replace />
    }
    return <FullPageError message={errorMessage(me.error)} onRetry={() => void me.refetch()} />
  }
  if (me.data.areas.length === 0) return <Navigate to="/no-access" replace />

  return (
    <SessionContext value={me.data}>
      <Outlet />
    </SessionContext>
  )
}

/** `/` → the first permitted area. */
export function AreaHomeRedirect() {
  const me = useSession()
  return <Navigate to={homePath(me) ?? '/no-access'} replace />
}

/** Wraps an area's routes in the shell, or redirects when the area isn't permitted. */
export function AreaRoute({ area }: { area: AreaId }) {
  const me = useSession()
  if (!me.areas.includes(area)) return <Navigate to={homePath(me) ?? '/no-access'} replace />
  return <AppShell area={area} />
}
